import { API_BASE } from '../../config';
import { ApiError, getErrorMessage } from './ApiError';
import { isGetRequest, makeRequestKey, getPendingRequest, setPendingRequest } from './RequestDeduper';
import { notifyUnauthorized } from './authEvents';

export { ApiError, getErrorMessage } from './ApiError';

export interface RequestConfig {
  headers?: Record<string, string>;
  retry?: number;
  retryDelay?: number;
  timeout?: number;
  signal?: AbortSignal;
}

export interface PollConfig {
  interval: number;
  maxAttempts: number;
  isComplete: (data: any) => boolean;
  isFailed?: (data: any) => boolean;
  onProgress?: (data: any, attempt: number) => void;
}

export const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class BaseService {
  protected baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_BASE;
  }

  protected async request<T>(
    path: string,
    options?: RequestInit,
    config?: RequestConfig
  ): Promise<T> {
    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    const maxRetries = config?.retry ?? 2;
    const retryDelay = config?.retryDelay ?? 1000;
    const controller = new AbortController();
    const timeoutId = config?.timeout
      ? setTimeout(() => controller.abort(), config.timeout)
      : null;

    const isFormData = options?.body instanceof FormData;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('bongai_token') : null;
    const fetchOptions: RequestInit = {
      ...options,
      signal: config?.signal || controller.signal,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...config?.headers,
        ...options?.headers,
      },
    };

    if (isGetRequest(options)) {
      const requestKey = makeRequestKey(url, fetchOptions);
      const existing = getPendingRequest(requestKey);
      if (existing) return existing as Promise<T>;

      const requestPromise = this._executeWithRetry<T>(url, fetchOptions, maxRetries, retryDelay);
      setPendingRequest(requestKey, requestPromise);

      try {
        return await requestPromise;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    try {
      return await this._executeWithRetry<T>(url, fetchOptions, maxRetries, retryDelay);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  private async _executeWithRetry<T>(
    url: string,
    options: RequestInit,
    maxRetries: number,
    retryDelay: number
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          let body: any;
          try {
            body = await response.json();
          } catch {
            body = await response.text().catch(() => null);
          }

          // FastAPI HTTPException 可传 dict detail（如 402 积分不足 {message, need, balance}）：
          // 对象型 detail 直接进消息链会 toString 成 [object Object]，先取其 message；
          // 字符串 detail 保持原位；完整 body 仍存入 ApiError.body 供 need/balance 等字段消费
          const rawDetail = body?.detail;
          const detailMessage =
            rawDetail && typeof rawDetail === 'object' && rawDetail.message
              ? String(rawDetail.message)
              : typeof rawDetail === 'string'
                ? rawDetail
                : undefined;
          const message =
            body?.error || body?.message || body?.msg || detailMessage || `HTTP ${response.status}`;
          // 登录态失效：全局广播（由 authStore 注册的 expireSession 统一清态 + 弹登录框）
          if (response.status === 401) notifyUnauthorized();
          throw new ApiError(message, response.status, body);
        }

        if (response.status === 204) return undefined as T;

        return await response.json();
      } catch (error: any) {
        lastError = error;

        if (error instanceof ApiError) {
          const isRetryable =
            error.status >= 500 || error.status === 429 || error.status === 408;
          if (!isRetryable || attempt >= maxRetries) throw error;

          const isOverloaded =
            error.status === 503 || error.status === 429;
          const delay = isOverloaded
            ? retryDelay * Math.pow(2, attempt)
            : retryDelay * (attempt + 1);

          console.warn(
            `[BaseService] ${error.status} on ${url}, retrying in ${delay}ms (${attempt + 1}/${maxRetries})`
          );
          await wait(delay);
        } else if (error.name === 'AbortError') {
          throw error;
        } else {
          if (attempt >= maxRetries) throw error;
          console.warn(
            `[BaseService] Network error on ${url}, retrying (${attempt + 1}/${maxRetries})`
          );
          await wait(retryDelay * (attempt + 1));
        }
      }
    }

    throw lastError!;
  }

  protected async poll<T>(
    url: string,
    config: PollConfig,
    requestConfig?: RequestConfig
  ): Promise<T> {
    for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
      const data = await this.request<T>(url, undefined, requestConfig);

      if (config.isFailed?.(data)) {
        throw new ApiError('Task failed', 500, data);
      }

      if (config.isComplete(data)) {
        return data;
      }

      config.onProgress?.(data, attempt);
      await wait(config.interval);
    }

    throw new ApiError('Polling timeout: max attempts reached', 408);
  }

  protected get<T = unknown>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, { method: 'GET' }, config);
  }

  protected post<T = unknown>(path: string, body?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(
      path,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    );
  }

  protected put<T = unknown>(path: string, body?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(
      path,
      {
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    );
  }

  protected delete<T = unknown>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' }, config);
  }

  protected upload<T = unknown>(path: string, formData: FormData, config?: RequestConfig): Promise<T> {
    const headers: Record<string, string> = { ...config?.headers };
    delete headers['Content-Type'];
    return this.request<T>(
      path,
      {
        method: 'POST',
        body: formData,
        headers,
      },
      { ...config, headers }
    );
  }
}
